import { config } from '../config';

export const pathResolve = (path: string): string => {
    return `/${path}`.replace(/\/{2,}/g, '/').replace(/(.+)(\/+)$/, '$1');
};

/**
 * Generates a localized link based on the provided language.
 *
 * @param link - The original link, which can be a string or undefined.
 * @param lang - The language code to prepend to the link if it starts with a '/'.
 * @param forceLang – If true, the language code will be prepended to the link even if NEXT_PUBLIC_USE_LOCALIZED_LINKS=0.
 * @returns A string representing the localized link. If the link is undefined or does not start with a '/',
 *          it returns the original link or '#' if the link is also undefined.
 */
export const getLocalizedLink = (
    link?: string,
    lang?: string,
    forceLang: boolean = false,
): string => {
    return isValidURL(link)
        ? getLocalizedAbsoluteLink(link)
        : getLocalizedRelativeLink(link, lang, forceLang);
};

const getLocalizedRelativeLink = (
    link?: string,
    lang?: string,
    forceLang: boolean = false,
): string => {
    const currentLanguage = lang || config.language.default!;
    const currentLink = link || '#';
    // If the site is not using localized links, return the original link.
    return forceLang || process.env.NEXT_PUBLIC_USE_LOCALIZED_LINKS === '1'
        ? pathResolve(`/${currentLanguage}/${currentLink}`)
        : pathResolve(`/${currentLink}`);
};

const getLocalizedAbsoluteLink = (link?: string) => link || '#';

const isValidURL = (url?: string): boolean => {
    try {
        new URL(url || '');
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Generates an absolute localized link based on the provided link and language.
 *
 * @param link - The relative link to be localized. If undefined, a fallback of '#' will be used.
 * @param lang - The language code to be included in the absolute URL.
 * @returns The absolute localized link if the link starts with '/', otherwise returns the original link or '#' if undefined.
 */
export const getAbsoluteLocalizedLink = (link: string | undefined, lang: string): string => {
    // TODO: aaply NEXT_PUBLIC_USE_LOCALIZED_LINKS
    return link?.charAt(0) === '/'
        ? process.env.NEXT_PUBLIC_USE_LOCALIZED_LINKS === '1'
            ? `${process.env.NEXT_PUBLIC_SITE_URL}/${lang}${link}`
            : `${process.env.NEXT_PUBLIC_SITE_URL}${link}`
        : (link ?? '#');
};
